import { Head } from "vite-react-ssg";
import { Link } from "react-router-dom";
import { Sparkles, Salad, BellRing, Bot, LineChart, Smartphone, Check } from "lucide-react";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";

const ACCENT = "#3730A3";

const FEATURES = [
  {
    icon: Sparkles,
    title: "Un programme complet, généré en quelques secondes",
    desc: "L'IA part du contexte de votre client — objectif, niveau, matériel — et propose une base structurée. Vous l'ajustez d'un coup d'œil au lieu d'y passer la matinée. Le copier-coller de séances, c'est fini.",
  },
  {
    icon: Salad,
    title: "Le plan nutrition qui va avec",
    desc: "Objectif calorique, préférences, contraintes : l'IA génère un plan cohérent avec l'entraînement, que vous modifiez à la main quand il faut. Plus de « meal plan » PDF que le client n'ouvre jamais.",
  },
  {
    icon: BellRing,
    title: "La relance est déjà rédigée quand le client décroche",
    desc: "Un client inactif dix jours, un check-in sans réponse : CoachOS le repère tout seul et vous propose le message. Vous relancez à temps, au lieu d'apprendre le départ par un SMS d'annulation.",
  },
  {
    icon: Bot,
    title: "Un assistant IA qui connaît vos clients",
    desc: "Un brouillon de réponse, un résumé de suivi, une idée d'adaptation : l'assistant a le contexte du client sous la main. Il accélère ce qui vous ralentit — vous gardez la main sur ce qui se dit.",
  },
  {
    icon: LineChart,
    title: "Votre coaching en chiffres clairs",
    desc: "Clients actifs, adhérence aux programmes, séances de la semaine : le tableau de bord vous dit où vous en êtes d'un coup d'œil — pas au feeling, pas le jour où un client part.",
  },
  {
    icon: Smartphone,
    title: "Le logiciel côté coach, l'app côté client",
    desc: "Vous travaillez depuis votre ordinateur ; vos clients reçoivent programmes, messages et suivi sur leur téléphone. Les deux moitiés du même outil.",
    to: "/application-coach-sportif",
    linkLabel: "Voir l'app côté client",
  },
];

const FAQ = [
  {
    q: "L'IA va-t-elle remplacer mon coaching ?",
    a: "Non, elle vous augmente. Elle accélère ce qui vous ralentit — génération de programmes, brouillons de relance, résumés de suivi — pendant que vous gardez la main sur les décisions, la relation et la qualité de l'accompagnement.",
  },
  {
    q: "Quel est le meilleur logiciel pour un coach sportif indépendant ?",
    a: "Celui qui réunit programmes, suivi, nutrition et communication au même endroit, et vous décharge du travail répétitif. C'est le parti pris de CoachOS, avec l'IA en renfort, dès 39 €/mois.",
  },
  {
    q: "Faut-il vraiment un logiciel quand on démarre ?",
    a: "Tant que vous avez trois clients, un tableur suffit. Le basculement se joue vers 8-10 clients : c'est là que les oublis commencent et qu'une heure de travail invisible par jour s'installe sans bruit.",
  },
  {
    q: "CoachOS remplace-t-il Excel et mes dossiers ?",
    a: "Oui, et surtout il les fait se parler. Programmes, suivi, nutrition et messages vivent au même endroit et restent à jour — vous ne recopiez plus jamais la même information deux fois.",
  },
  {
    q: "CoachOS gère-t-il la facturation ?",
    a: "Pas pour l'instant : la facturation d'un indépendant se gère mieux avec un outil comptable dédié, surtout avec la réforme de la facture électronique qui arrive. CoachOS se concentre sur le cœur de votre métier — le coaching. Chaque outil sur son terrain.",
  },
  {
    q: "Combien de temps pour tout mettre en place ?",
    a: "Une dizaine de minutes pour importer vos clients et générer un premier programme. Si la mise en route prenait un week-end, personne ne le ferait — nous compris.",
  },
];

const LogicielCoachSportif = () => (
  <div className="min-h-screen" style={{ background: "#ffffff" }}>
    <Head>
      <title>Logiciel pour coach sportif augmenté par l'IA — CoachOS</title>
      <meta name="description" content="Le logiciel de coaching où l'IA génère vos programmes, rédige vos relances et repère les clients qui décrochent. Vous gardez la main. Dès 39 €/mois." />
      <link rel="canonical" href="https://www.coach-os.eu/logiciel-coach-sportif" />
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          name: "CoachOS",
          applicationCategory: "BusinessApplication",
          operatingSystem: "Web, iOS, Android",
          description: "Logiciel pour coach sportif augmenté par l'IA : génération de programmes et de plans nutrition, détection des clients qui décrochent, assistant IA et suivi.",
          offers: { "@type": "Offer", price: "39", priceCurrency: "EUR" },
        })}
      </script>
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQ.map(({ q, a }) => ({
            "@type": "Question",
            name: q,
            acceptedAnswer: { "@type": "Answer", text: a },
          })),
        })}
      </script>
    </Head>
    <Navbar />
    <main className="container max-w-5xl pt-32 pb-24">
      {/* Hero aligné à gauche */}
      <p className="mb-3 text-sm font-semibold uppercase tracking-wider text-[#3730A3]">Logiciel pour coach sportif</p>
      <h1 className="max-w-3xl text-4xl md:text-6xl font-extrabold tracking-tighter leading-none text-[#0e1220] mb-6">
        Le logiciel de coaching qui met l'IA au travail — <span style={{ color: ACCENT }}>pas à votre place</span>.
      </h1>
      <p className="max-w-[56ch] text-lg text-[#0e1220]/60 leading-relaxed mb-8">
        Générer un programme, rédiger une relance, repérer le client qui s'essouffle : ce qui vous
        prenait une matinée, l'IA de CoachOS le prépare en quelques secondes. Vous relisez, vous
        ajustez, vous validez. La décision reste la vôtre.
      </p>
      <div className="flex flex-col sm:flex-row gap-3 mb-8">
        <Link to="/#pricing" className="inline-flex h-12 items-center justify-center rounded-full bg-[#14142B] px-7 font-semibold text-white shadow-[0_10px_24px_rgba(20,20,43,0.18)] transition hover:bg-[#23233d] active:scale-[0.98]">
          Commencer — dès 39 €/mois
        </Link>
        <a href="https://calendly.com/gregory-scafarto/30min" target="_blank" rel="noopener noreferrer"
          className="inline-flex items-center justify-center rounded-full px-6 h-12 font-semibold text-[#0e1220]/90 border border-[#dfe2ec] bg-white hover:bg-[#eef0f6]">
          Demander une démo
        </a>
      </div>

      {/* Bandeau confiance */}
      <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-[#0e1220]/50 mb-20">
        {["100 % français, pensé pour le coaching", "Données hébergées en Europe", "L'IA à chaque étape, vous gardez la main", "Sans engagement"].map(t => (
          <span key={t} className="inline-flex items-center gap-1.5"><Check className="w-4 h-4" style={{ color: ACCENT }} />{t}</span>
        ))}
      </div>

      {/* Le problème */}
      <div className="max-w-[62ch] mb-20">
        <h2 className="text-2xl md:text-3xl font-extrabold tracking-tight text-[#0e1220] mb-4">
          Ce qui remplit vos soirées, ce n'est pas le coaching.
        </h2>
        <p className="text-[#0e1220]/60 leading-relaxed mb-4">
          C'est tout le reste. Le programme à écrire pour lundi, la relance à taper pour le client
          qu'on n'a pas vu depuis dix jours, le plan nutrition à recaler, les trois messages restés
          sans réponse. Chaque heure de séance cache vingt à trente minutes de travail invisible —
          et c'est lui qui déborde sur vos soirées.
        </p>
        <p className="text-[#0e1220]/60 leading-relaxed">
          Un logiciel qui range mieux vos fichiers ne règle rien. <strong className="text-[#0e1220] font-semibold">Il faut un
          outil qui fasse le premier jet à votre place</strong> — et vous laisse la partie qui compte :
          décider, ajuster, accompagner.
        </p>
      </div>

      {/* Fonctionnalités — zig-zag 2 colonnes */}
      <div className="grid gap-x-14 gap-y-12 md:grid-cols-2 mb-20">
        {FEATURES.map(({ icon: Icon, title, desc, to, linkLabel }) => (
          <div key={title}>
            <div className="mb-3 grid h-10 w-10 place-items-center rounded-xl" style={{ background: `${ACCENT}22`, color: ACCENT }}>
              <Icon className="w-5 h-5" strokeWidth={2} />
            </div>
            <h2 className="text-lg font-bold text-[#0e1220] mb-2">{title}</h2>
            <p className="text-[#0e1220]/55 leading-relaxed max-w-[52ch]">{desc}</p>
            {to && (
              <Link to={to} className="mt-2 inline-flex items-center gap-1 text-sm font-semibold" style={{ color: ACCENT }}>
                {linkLabel} →
              </Link>
            )}
          </div>
        ))}
      </div>

      {/* Prix */}
      <div className="max-w-[62ch] mb-20">
        <h2 className="text-2xl md:text-3xl font-extrabold tracking-tight text-[#0e1220] mb-4">
          Un prix affiché, pour une fois. IA comprise.
        </h2>
        <p className="text-[#0e1220]/60 leading-relaxed mb-5">
          La plupart des logiciels pour coach sportif cachent leur tarif derrière un « demandez une
          démo ». On préfère l'annoncer : <strong className="text-[#0e1220] font-semibold">39 €/mois, clients illimités,
          l'IA incluse dès le premier plan.</strong> Pas de palier qui se déclenche à votre dixième
          client, pas d'assistant facturé à part. Rapporté à un seul client retenu grâce à une
          relance envoyée à temps, c'est déjà rentabilisé.
        </p>
        <Link to="/#pricing" className="inline-flex h-11 items-center rounded-full bg-[#14142B] px-6 font-semibold text-white shadow-[0_8px_20px_rgba(20,20,43,0.16)] transition hover:bg-[#23233d]">
          Voir les tarifs
        </Link>
      </div>

      {/* FAQ */}
      <h2 className="text-2xl font-extrabold tracking-tight text-[#0e1220] mb-8">Questions fréquentes</h2>
      <div className="divide-y divide-[#e7e9f1] mb-16">
        {FAQ.map(({ q, a }) => (
          <div key={q} className="py-6">
            <h3 className="font-semibold text-[#0e1220] mb-2">{q}</h3>
            <p className="text-[#0e1220]/55 leading-relaxed max-w-[70ch]">{a}</p>
          </div>
        ))}
      </div>

      {/* CTA final */}
      <div className="rounded-2xl border border-[#e7e9f1] bg-white shadow-[0_1px_3px_rgba(16,20,40,0.05)] p-8">
        <h2 className="text-xl font-bold text-[#0e1220] mb-2">Rendez à l'IA le travail répétitif. Gardez le coaching.</h2>
        <p className="text-[#0e1220]/55 mb-5">Programmes, suivi, relances et nutrition, préparés par l'IA et validés par vous. Prêt en dix minutes, dès 39 €/mois.</p>
        <Link to="/#pricing" className="inline-flex h-11 items-center rounded-full bg-[#14142B] px-6 font-semibold text-white shadow-[0_8px_20px_rgba(20,20,43,0.16)] transition hover:bg-[#23233d]">
          Commencer maintenant
        </Link>
      </div>
    </main>
    <Footer />
  </div>
);

export default LogicielCoachSportif;
